import { useEffect, useState } from "react";

import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { AttendanceHistoryTimeline } from "./AttendanceHistoryTimeline.js";
import type { CorrectionRequest } from "./PendingAttendanceCorrectionsQueue.js";
import type { AttendanceRecord, MyCorrection } from "../pages/AttendancePage.js";
import { currentAnchor, rangeFor, shiftAnchor, type HistoryPeriod } from "../lib/historyRange.js";

/**
 * An approver opens this on one person, so it starts on the current month rather than
 * My History's current week: they are reviewing a month of attendance, not today's.
 */
const INITIAL_PERIOD: HistoryPeriod = "month";

/** The period a custom range borrows its anchor arithmetic from, as My History does. */
const CUSTOM_BASE = "week";

/**
 * One employee's attendance as the same month register My History draws, for the
 * member panel an approver reaches from All Team, Team, or a department roster.
 *
 * It owns its own range and day selection rather than taking them as props:
 * the panel it sits in is already long, and an approver's browsing of one person's
 * months has nothing to do with the state of the viewer's own My History tab.
 *
 * `correctionEditor` is deliberately not passed to the timeline, which makes the rows
 * read-only: an approver reviews the record and actions requests from the correction
 * queue above, they do not file the employee's correction request for them.
 */
export function MemberAttendanceHistory({ employeeId }: { employeeId: string }) {
  const [period, setPeriod] = useState<HistoryPeriod>(INITIAL_PERIOD);
  const [anchor, setAnchor] = useState(() => currentAnchor(INITIAL_PERIOD));
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const [history, setHistory] = useState<AttendanceRecord[]>([]);
  const [hasAnyRecords, setHasAnyRecords] = useState(false);
  const [corrections, setCorrections] = useState<MyCorrection[]>([]);
  const [biometric, setBiometric] = useState<Map<string, { checkIn: string | null; checkOut: string | null }>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const derived = rangeFor(period === "custom" ? CUSTOM_BASE : period, anchor);
  const from = period === "custom" ? customFrom : derived.from;
  const to = period === "custom" ? customTo : derived.to;

  useEffect(() => {
    // A half-filled custom range would 400; wait for both dates.
    if (!from || !to || from > to) { setLoading(false); setHistory([]); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ limit: "100", from, to });
    api.get<{ data: AttendanceRecord[]; hasAnyRecords: boolean }>(`/attendance/${employeeId}?${params}`)
      .then((r) => {
        if (cancelled) return;
        setHistory(r.data.data);
        setHasAnyRecords(r.data.hasAnyRecords);
      })
      .catch((err) => {
        if (cancelled) return;
        logError("MemberAttendanceHistory.load", err);
        setError(errMsg(err) ?? "Failed to load this employee's attendance");
        setHistory([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [employeeId, from, to]);

  // Device punches for the same range, shown beside the app's own times. Settled on
  // its own: an employee with no device mapping (or a reader that was down) still gets
  // the record, just with the biometric columns blank.
  useEffect(() => {
    if (!from || !to || from > to) { setBiometric(new Map()); return; }
    let cancelled = false;
    api.get<{ mapped: boolean; days: Array<{ date: string; checkIn: string | null; checkOut: string | null }> }>(
      `/attendance/${employeeId}/biometric-range?from=${from}&to=${to}`,
    )
      .then((r) => {
        if (cancelled) return;
        setBiometric(new Map(r.data.days.map((d) => [d.date, { checkIn: d.checkIn, checkOut: d.checkOut }])));
      })
      .catch((err) => {
        if (cancelled) return;
        logError("MemberAttendanceHistory.biometric", err);
        setBiometric(new Map());
      });
    return () => { cancelled = true; };
  }, [employeeId, from, to]);

  // Settled on its own: a viewer who may read the attendance but not this person's
  // correction queue still gets the record, just without the approval columns.
  useEffect(() => {
    let cancelled = false;
    api.get<{ data: CorrectionRequest[] }>(`/attendance/corrections?employeeId=${employeeId}&limit=100`)
      .then((r) => { if (!cancelled) setCorrections(r.data.data.map(asMyCorrection)); })
      .catch((err) => {
        if (cancelled) return;
        logError("MemberAttendanceHistory.corrections", err);
        setCorrections([]);
      });
    return () => { cancelled = true; };
  }, [employeeId]);

  return (
    <>
      {error && (
        <div className="mb-2 rounded-md bg-[var(--color-error-bg)] px-3 py-2 text-[13px] text-[var(--color-error-fg)]">
          {error}
        </div>
      )}
      <AttendanceHistoryTimeline
        // Fixed to the register, with no switcher: this panel is a month-at-a-glance
        // read of one person, and clicking a day already drops its full row below.
        view="register"
        biometricByDate={biometric}
        loading={loading}
        history={history}
        hasAnyRecords={hasAnyRecords}
        myCorrections={corrections}
        period={period}
        onPeriodChange={(p) => { setPeriod(p); setAnchor(currentAnchor(p === "custom" ? CUSTOM_BASE : p)); }}
        rangeLabel={period === "custom" ? "Custom range" : derived.label}
        onPrev={() => setAnchor((a) => shiftAnchor(period === "custom" ? CUSTOM_BASE : period, a, -1))}
        onNext={() => setAnchor((a) => shiftAnchor(period === "custom" ? CUSTOM_BASE : period, a, 1))}
        customFrom={customFrom}
        customTo={customTo}
        onCustomFromChange={setCustomFrom}
        onCustomToChange={setCustomTo}
        rangeFrom={from}
        rangeTo={to}
        registerEmployeeId={employeeId}
      />
    </>
  );
}

/**
 * The correction queue's row as the timeline's own correction shape. They are the same
 * record read by two endpoints -- `mine=true` for My History, `employeeId=` here -- so
 * this is a rename, not a conversion.
 */
function asMyCorrection(c: CorrectionRequest): MyCorrection {
  return {
    id: c.id,
    date: c.date,
    status: c.status,
    requestedCheckIn: c.requestedCheckIn,
    requestedCheckOut: c.requestedCheckOut,
    reason: c.reason,
    approverRole: c.approverRole,
    approverRemark: c.approverRemark ?? null,
  };
}
